import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, model, OnDestroy, OnInit, viewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonName } from '@app/components/booking-flow/booking-flow.component';
import { EmployeeSelectionSource } from '@app/enums/employeeSelectionSource.enum';
import { LSKey } from '@app/enums/localStorageKey.enum';
import { AppointmentRequest } from '@app/models/appointmentRequest.model';
import { Employee } from '@app/models/employee.model';
import { SiteBookingSettings } from '@app/models/siteBookingSettings.model';
import { EmployeeService } from '@app/services/employee.service';
import { FeatureFlagService, FeatureKey } from '@app/services/feature-flag.service';
import { APPOINTMENT_REQUEST, CUSTOMER_ID, LocalStorageService, SITE_BOOKING_SETTINGS } from '@app/services/local-storage.service';
import { LocaleService } from '@app/services/locale.service';
import { SiteService } from '@app/services/site.service';
import { Service } from '@models/service.model';
import { provideIcons } from '@ng-icons/core';
import { lucideCheck, lucidePlus } from '@ng-icons/lucide';
import { ServiceService } from '@services/service.service';
import { BrnSheet } from '@spartan-ng/brain/sheet';
import { firstValueFrom, from, Observable, of, Subject, Subscription, takeUntil } from 'rxjs';
import { IBookingStepComponent } from '../IBookingStepComponent';
import { ServiceCategoryType } from './service-category-type';

@Component({
    selector: 'app-add-service',
    templateUrl: './add-service.component.html',
    styleUrls: ['./add-service.component.css'],
    standalone: false,
    providers: [provideIcons({ lucidePlus, lucideCheck })],
})
export class AddServiceComponent implements OnInit, OnDestroy, AfterViewInit, IBookingStepComponent {
    public formGroup: FormGroup;
    public ngUnsubscribe: Subject<any> = new Subject();
    private _destroyed$: Subject<void>;
    private navigationButtonSubscription!: Subscription;
    public isServicePriceAndDurationFeatureActive: boolean = false;
    serviceList: Service[] = [];
    categoryList: string[] = [];
    filteredServiceList: Service[] = [];
    selectedCategory: string;
    shouldShowServicePrice = false;
    isBookMultiServiceEnabled = false;
    currencyCode: string = '';
    public selectedServices: Service[] = [];
    appointmentRequest: AppointmentRequest;
    public get serviceFormControl() {
        return (this.formGroup?.controls['service'] as FormControl) || null;
    }
    private isRedesignFeatureActive: boolean = false;
    groupFilteredServiceList: { key: string; services: Service[] }[] = [];
    employees: Employee[] = [];
    private otherServicesCategory = 'Other Services';
    public employeeSelectionSheet = viewChild<BrnSheet>('employeeSelection');
    public selectedEmployee!: Employee;
    public filtersStickyContainer = viewChild<ElementRef<HTMLDivElement>>('filtersStickyContainer');
    public apptRequestUpdatedSubscription!: Subscription;
    private allEmployeesObject: Employee = {
        id: '0',
        name: 'All Employees',
        employeeServicePriceList: [],
    };
    isEmployeeLoading = true;
    isValidEmployeeAndService = true;
    public isLoading = true;

    constructor(
        private router: Router,
        private serviceService: ServiceService,
        private siteService: SiteService,
        private fb: FormBuilder,
        private localStorageService: LocalStorageService,
        private featureFlagService: FeatureFlagService,
        private cdRef: ChangeDetectorRef,
        private localeService: LocaleService,
        private employeeService: EmployeeService
    ) {
        this._destroyed$ = new Subject();
        this.appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        this.selectedCategory = ServiceCategoryType.ALL;
        this.isServicePriceAndDurationFeatureActive = this.featureFlagService.getFeatureFlag(FeatureKey.ShowPriceAndDuration);
        this.isBookMultiServiceEnabled = this.featureFlagService.getFeatureFlag(FeatureKey.BookMultiService);
        this.isRedesignFeatureActive = this.featureFlagService.getFeatureFlag(FeatureKey.Redesign);
        this.selectedEmployee = this.allEmployeesObject;
        this.formGroup = this.fb.group({
            service: new FormControl('', Validators.required),
        });
        const siteBookingSettings = this.localStorageService.get(SITE_BOOKING_SETTINGS) as SiteBookingSettings;
        this.titleBarSubtitle = siteBookingSettings?.businessName;
        if (this.featureFlagService.getFeatureFlag(FeatureKey.DSBRequestOnly) && !!siteBookingSettings?.requestAppointmentOnly) {
            this.titleBarTitle = 'Request Appointment with';
        }
    }

    async ngOnInit() {
        this.setIsLoading(true);
        await this.getSiteBookingSettings();
        await this.fetchServices();
        /* istanbul ignore next */
        this.navigationButtonSubscription = this.getNavigationButtonChangeObservable().subscribe((buttonName) => {
            if (buttonName === ButtonName.SelectEmployee) {
                if (!this.isBookMultiServiceEnabled) {
                    this.selectedServices = this.serviceList.filter((s) => s.id === this.formGroup.value.service);
                } else {
                    this.selectedServices = this.serviceList.filter((s) => this.selectedServices.map((srv) => srv.id).includes(s.id));
                    this.setSelectedServicesFormGroupValue();
                }
                this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Service, this.selectedServices);
            }
        });
        this.formGroup.statusChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((status) => {
            if (status === 'VALID') {
                this.setIsStepFormValid(true);
            } else if (status === 'INVALID') {
                this.setIsStepFormValid(false);
            }
        });

        if (!this.isRedesignFeatureActive) {
            this.previouslySelectedService();
            this.setIsLoading(false);
        }

        this.apptRequestUpdatedSubscription = this.localStorageService.appointmentRequestUpdated
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(({ delta }) => {
                if (delta.updated?.service || delta.added?.service || delta.deleted?.service) {
                    this.appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
                    this.selectedServices = this.appointmentRequest?.service || [];
                }
            });
    }

    protected setIsLoading(isLoading: boolean): void {
        this.isLoading = isLoading;
    }
    protected setIsStepFormValid(isValid: boolean): void {
        // No implementation needed here for this component, it exists to be overridden in the classic version
    }
    protected getNavigationButtonChangeObservable(): Observable<string> {
        // No real implementation needed here for this component, it exists to be overridden in the classic version
        return of('');
    }

    // Implementation of IBookingStepComponent
    public continueButtonText: string = ButtonName.SelectDateTime;
    public nextStepRoute: string = 'datetime';
    public allowSummarySheetDelete: boolean = true;
    public titleBarTitle = 'Booking with';
    public titleBarSubtitle: string | undefined;
    public showTitleBarAccountSection = true;
    public titleBarHeight = model<string>();
    public isValid(): boolean {
        const appointment = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        return appointment?.service?.length > 0 && appointment?.employee?.length > 0;
    }

    ngAfterViewInit() {
        // The filter container needs to stick right below the title bar when scrolling, but the
        // title bar's height is dynamic, so we can't set the filter container's sticky top beforehand/
        // Luckily, the scrollable area is the viewport (instead of some inner div), so we can determine
        // the distance from the top of the viewport and just set the 'top' style to that so it will never
        // scroll higher than where it initially renders (right below the title bar).
        requestAnimationFrame(() => {
            const filterContainer = this.filtersStickyContainer()?.nativeElement;
            if (filterContainer) {
                const filterContainerTop = filterContainer.getBoundingClientRect().top;
                filterContainer.style.setProperty('top', `${filterContainerTop}px`);
            }
        });
    }

    toggleService(service: any) {
        if (service.checked) {
            this.selectedServices.push(this.serviceList.filter((s) => s.id === service.source.value)[0]);
        } else {
            this.selectedServices = this.selectedServices.filter((s) => s.id !== service.source.value);
        }
        this.setSelectedServicesFormGroupValue();
    }

    private setSelectedServicesFormGroupValue() {
        const selectedServicesString = this.selectedServices.map((service) => service.id).join(',');
        this.formGroup.get('service')?.patchValue(selectedServicesString);
    }

    checkSelectedServices(serviceId: string) {
        const selectedServicesIds = this.selectedServices.map((service) => service.id);
        return selectedServicesIds.includes(serviceId);
    }

    private async getSiteBookingSettings(): Promise<void> {
        const siteBookingSettings = this.localStorageService.get(SITE_BOOKING_SETTINGS) as SiteBookingSettings;

        if (siteBookingSettings) {
            this.shouldShowServicePrice = this.isServicePriceAndDurationFeatureActive && siteBookingSettings.showPricesWithServices;
            await this.localeService.loadLocaleFile(siteBookingSettings.customerCultureCode, true);
            this.currencyCode = this.localeService.getCurrencyCode() || 'USD';
        } else {
            const customerID = this.localStorageService.getString(CUSTOMER_ID);
            const result = await firstValueFrom(this.siteService.getSiteBookingSettings(customerID).pipe(takeUntil(this._destroyed$)));

            this.localStorageService.set(SITE_BOOKING_SETTINGS, result);
            this.shouldShowServicePrice = this.isServicePriceAndDurationFeatureActive && result.showPricesWithServices;
            await this.localeService.loadLocaleFile(result.customerCultureCode, true);
            this.currencyCode = this.localeService.getCurrencyCode() || 'USD';
        }
    }

    private async fetchServices(): Promise<void> {
        const serviceList = await firstValueFrom(from(this.serviceService.getServiceList()).pipe(takeUntil(this._destroyed$)));

        this.serviceList = serviceList.filter(
            (service) => !service.categoryList.includes(ServiceCategoryType.DAYCARE) && service.employeeIDList?.length
        );

        this.filteredServiceList = [...this.serviceList].sort((a, b) => a.name.localeCompare(b.name));

        if (this.isRedesignFeatureActive) {
            const employeeIdList = Array.from(new Set(this.filteredServiceList.flatMap((service) => service.employeeIDList ?? [])));
            const selectedServicesIds = this.filteredServiceList.map((service) => service.id);
            await this.fetchEmployees(employeeIdList, selectedServicesIds);
        }

        this.getCategories();
        this.groupServicesByCategory();
        this.formGroup.updateValueAndValidity();
    }

    private groupServicesByCategory() {
        if (this.isRedesignFeatureActive) {
            const grouped = this.filteredServiceList.reduce(
                (acc, service) => {
                    // Filter out empty categories
                    const validCategories = service.categoryList?.filter((cat) => cat.trim() !== '') || [];
                    const categories = validCategories.length ? validCategories : [this.otherServicesCategory];

                    categories.forEach((category) => {
                        (acc[category] ||= []).push(service);
                    });

                    return acc;
                },
                {} as Record<string, Service[]>
            );

            // Convert to array and sort services by web order within each category
            this.groupFilteredServiceList = Object.entries(grouped).map(([key, services]) => {
                const withOrder = services.filter((s) => s.webOrder !== null).sort((a, b) => (a.webOrder ?? 0) - (b.webOrder ?? 0));
                const withoutOrder = services.filter((s) => s.webOrder === null).sort((a, b) => a.name.localeCompare(b.name));

                return {
                    key,
                    services: [...withOrder, ...withoutOrder],
                };
            });

            // Ensure otherServicesCategory is at the end of categoryList
            if (
                !this.categoryList.includes(this.otherServicesCategory) &&
                this.groupFilteredServiceList.some((group) => group.key === this.otherServicesCategory)
            ) {
                this.categoryList = [...this.categoryList, this.otherServicesCategory];
            }

            // Sort categories according to categoryList order, ensuring "Other Services" is always placed last
            this.groupFilteredServiceList.sort((a, b) => {
                if (a.key === this.otherServicesCategory) {
                    return 1;
                }
                if (b.key === this.otherServicesCategory) {
                    return -1;
                }

                return this.categoryList.indexOf(a.key) - this.categoryList.indexOf(b.key);
            });
        }
    }

    private getCategories() {
        const list = this.isRedesignFeatureActive ? this.filteredServiceList : this.serviceList;
        const categories = [...new Set(list.flatMap((s) => s.categoryList).filter((c) => c !== ''))];
        this.categoryList = this.isRedesignFeatureActive ? categories : [...new Set([...this.categoryList, ...categories])];
        const siteBookingSettings = this.localStorageService.get(SITE_BOOKING_SETTINGS) as SiteBookingSettings;
        const serviceCategoryOrder = siteBookingSettings?.serviceCategoryOrder;
        if (serviceCategoryOrder) {
            const orderedCategories = serviceCategoryOrder.split('§');
            const sortedCategories: string[] = [];
            orderedCategories.forEach((category) => {
                if (this.categoryList.includes(category)) {
                    sortedCategories.push(category);
                }
            });
            this.categoryList = sortedCategories;
        } else {
            this.categoryList.sort((a, b) => a.localeCompare(b));
        }

        if (!this.isRedesignFeatureActive) {
            this.categoryList.unshift(ServiceCategoryType.ALL);
        }
        this.shoudHideRightArrow();
    }

    /* istanbul ignore next */
    private shoudHideRightArrow() {
        this.cdRef.detectChanges();
        const arrowIcons = document.querySelectorAll('.icon .mat-icon');
        const toggleGroup = document.querySelector('.horizontal-scroll-box') as HTMLElement;

        if (!arrowIcons.length || !arrowIcons[1].parentElement) {
            return;
        }

        const maxScrollableWidth = toggleGroup.scrollWidth - toggleGroup.clientWidth;
        const isAtEnd = maxScrollableWidth <= 0;
        arrowIcons[1].parentElement.style.display = isAtEnd ? 'none' : 'flex';
    }

    public onCategoryChange($event: any) {
        const category = $event.value;
        const isAll = category === ServiceCategoryType.ALL;
        const isOther = category === this.otherServicesCategory;
        const isFirstAvailable = this.selectedEmployee.id === this.allEmployeesObject.id;

        // Handle same-category deselection (only in redesign mode)
        if (this.isRedesignFeatureActive && this.selectedCategory === category) {
            this.selectedCategory = '';
            this.filteredServiceList = [...this.serviceList]
                .filter((s) => isFirstAvailable || s.employeeIDList?.includes(this.selectedEmployee.id))
                .sort((a, b) => a.name.localeCompare(b.name));

            this.groupServicesByCategory();
            return;
        }

        // Category based filtering
        if (!isAll) {
            // Redesigned filtering logic
            if (this.isRedesignFeatureActive) {
                this.filteredServiceList = this.serviceList.filter((s) => {
                    const matchesCategory = isOther
                        ? !s.categoryList?.length || !s.categoryList.some((cat) => cat.trim())
                        : s.categoryList.includes(category);
                    const matchesEmployee = isFirstAvailable || s.employeeIDList?.includes(this.selectedEmployee.id);
                    return matchesCategory && matchesEmployee;
                });
            } else {
                // Legacy filtering logic
                this.filteredServiceList = this.serviceList.filter((s) => s.categoryList.includes(category));
            }

            // Sort by webOrder first, then by name
            const withOrder = this.filteredServiceList
                .filter((s) => s.webOrder !== null)
                .sort((a, b) => (a.webOrder ?? 0) - (b.webOrder ?? 0));

            const withoutOrder = this.filteredServiceList.filter((s) => s.webOrder === null).sort((a, b) => a.name.localeCompare(b.name));

            this.filteredServiceList = [...withOrder, ...withoutOrder];
        } else {
            // Show all services (sorted by name)
            this.filteredServiceList = [...this.serviceList].sort((a, b) => a.name.localeCompare(b.name));
        }

        // Update selected category and regroup
        this.selectedCategory = category;
        this.groupServicesByCategory();
    }

    isRadioButtonCheckedAndDepositRequired(serviceId: string): boolean {
        const service = this.serviceList.filter((s) => s.id === serviceId);
        const depositRequired = service[0].depositRequired ?? false;
        const depositPercentage = service[0].depositPercentage ?? 0;
        const depositFixedAmount = service[0].depositFixedAmount ?? 0;
        const selectedServiceId = this.formGroup.get('service')?.value;
        const selectedService = this.selectedServices.find((service) => service.id === serviceId);
        return (
            depositRequired && (depositPercentage > 0 || depositFixedAmount > 0) && (selectedServiceId === serviceId || !!selectedService)
        );
    }

    limitMaxLength(val: string) {
        return val?.length > 40 ? val.substring(0, 40) + '...' : val;
    }

    public showSelectedCategoryServicesCount(category: string) {
        return this.isBookMultiServiceEnabled && this.countSelectedServices(category) > 0;
    }

    public countSelectedServices(category: string) {
        return (
            this.selectedServices?.filter((service) => {
                const categories = service?.categoryList.filter((x) => x.trim().length !== 0) ?? [];

                // Handle "Other Services" category
                if (this.isRedesignFeatureActive && category === this.otherServicesCategory) {
                    return categories.length === 0;
                }

                // Handle normal category
                return categories.some((c) => c === category);
            })?.length ?? 0
        );
    }

    private previouslySelectedService() {
        if (this.appointmentRequest?.service?.length > 0) {
            if (!this.isBookMultiServiceEnabled) {
                this.formGroup.patchValue({ service: this.appointmentRequest.service[0].id });
                this.selectCategoryAndScrollToSelectedService(this.appointmentRequest.service[0]);
            } else {
                this.selectedServices = this.appointmentRequest.service;
                const selectedServicesIdsString = this.appointmentRequest.service.map((service) => service.id).join(',');
                this.formGroup.patchValue({ service: selectedServicesIdsString });
            }

            this.setIsStepFormValid(true);
        } else {
            this.setIsStepFormValid(false);
        }
    }

    private selectCategoryAndScrollToSelectedService(selectedService: Service) {
        setTimeout(() => {
            const categoryToSelect = this.categoryList.filter((category) => selectedService.categoryList.includes(category))[0];
            this.selectedCategory = categoryToSelect ?? ServiceCategoryType.ALL;
            this.onCategoryChange({ value: this.selectedCategory });
            const id = `service-${selectedService.id}`;
            const element = document.getElementById(id) as HTMLElement;
            /* istanbul ignore next */
            element?.scrollIntoView?.({ behavior: 'smooth' });
        }, 500);
    }

    ngOnDestroy() {
        this.navigationButtonSubscription?.unsubscribe();
        this.ngUnsubscribe.complete();
        this._destroyed$.next();
        this._destroyed$.complete();
        if (this.apptRequestUpdatedSubscription) {
            this.apptRequestUpdatedSubscription.unsubscribe();
        }
    }

    public addService(service: Service) {
        if (this.isBookMultiServiceEnabled) {
            if (!this.isServiceSelected(service)) {
                this.selectedServices.push(service);
            } else {
                return;
            }
        } else {
            this.selectedServices = [service];
        }
        this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Service, this.selectedServices);

        const apptRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        const isNoEmployeeSelected = !apptRequest?.employee?.length;
        const isAllEmployeeSelected = apptRequest?.employee?.[0]?.id === this.allEmployeesObject.id;

        if ((isNoEmployeeSelected || isAllEmployeeSelected) && this.selectedServices.length === 1) {
            this.employeeSelectionSheet()?.open();
            return;
        }

        this.updateServiceFormAndEmployeePrices();
    }

    public toggleServiceSelection(service: Service) {
        if (this.isServiceSelected(service)) {
            this.removeService(service);
        } else {
            this.addService(service);
        }
    }

    public removeService(service: Service) {
        this.selectedServices = this.selectedServices.filter((s) => s.id !== service.id);

        if (this.selectedServices.length === 0) {
            this.localStorageService.removeNested(APPOINTMENT_REQUEST, LSKey.Service);
            this.localStorageService.removeNested(APPOINTMENT_REQUEST, LSKey.Employee);
            this.formGroup.get('service')?.patchValue('');
            this.selectedEmployee = this.allEmployeesObject;
        } else {
            this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Service, this.selectedServices);
            this.updateServiceFormAndEmployeePrices();
        }
    }

    private updateServiceFormAndEmployeePrices(): void {
        this.setSelectedServicesFormGroupValue();
        const selectedServiceIds = this.selectedServices.map((s) => s.id);
        const isAllEmployeeSelected = this.appointmentRequest?.employee?.[0]?.id === this.allEmployeesObject.id;

        if (isAllEmployeeSelected) {
            this.selectedEmployee.employeeServicePriceList =
                this.employeeService.getEmployeeServicePriceListForFirstAvailability(selectedServiceIds);
            this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Employee, [this.selectedEmployee]);
        }

        this.setEmployeeToLocalStorage();
    }

    private async fetchEmployees(employeeIdList: string[], serviceIdList: string[]): Promise<void> {
        try {
            this.employees = await this.employeeService.getEmployeeList(employeeIdList, serviceIdList);
            this.employees = this.employeeService.sortEmployeeList(this.employees);
            this.allEmployeesObject.employeeServicePriceList =
                this.employeeService.getEmployeeServicePriceListForFirstAvailability(serviceIdList);
            // Only add All Employees (First Available) if there are 2+ actual employees
            if (this.employees.length > 1) {
                this.employees.unshift(this.allEmployeesObject);
            }

            this.isEmployeeLoading = false;
            const apptRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;

            const selectedEmployee = this.employees.find((x) => x.id === apptRequest?.employee?.[0]?.id);

            if (selectedEmployee) {
                this.selectedEmployee = selectedEmployee;
                this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Employee, [selectedEmployee]);
            } else if (this.employees.length === 1) {
                // Auto-select the only employee if there's just one
                this.selectedEmployee = this.employees[0];
                this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Employee, [this.selectedEmployee]);
            } else {
                this.selectedEmployee = this.allEmployeesObject; // Set default to "All Employees"
            }

            this.onEmployeeChange(this.selectedEmployee.id);

            this.setIsLoading(false);
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    }

    public isServiceSelected(service: Service) {
        return this.selectedServices?.some((s) => s.id === service.id) ?? false;
    }

    public onEmployeeChange(employeeId: string | string[] | undefined, source = EmployeeSelectionSource.Dropdown) {
        this.appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;

        this.resetSelectedServices(employeeId as string, source);
        this.resetSelectedEmployee(employeeId as string, source);
        this.resetFilteredServiceList();

        this.selectedCategory = '';
        void this.getCategories();
        this.groupServicesByCategory();
    }

    private resetSelectedServices(employeeId: string, source: EmployeeSelectionSource) {
        const clearServices = () => {
            this.selectedServices = [];
            this.localStorageService.removeNested(APPOINTMENT_REQUEST, LSKey.Service);
        };

        const appointment = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        const employeeIdFromLocal = appointment?.employee?.[0]?.id;
        const servicesFromLocal = appointment?.service;

        const filterServices = (ids: string[], empId: string) =>
            this.filteredServiceList.filter((s) => ids.includes(s.id) && (empId === '0' || s.employeeIDList?.includes(empId)));

        if (source === EmployeeSelectionSource.Dropdown) {
            if ((!employeeIdFromLocal && servicesFromLocal) || employeeIdFromLocal !== employeeId) {
                return clearServices();
            }

            if (employeeIdFromLocal === this.allEmployeesObject.id) {
                this.selectedServices = servicesFromLocal ?? [];
                return;
            }

            const selectedIds = servicesFromLocal?.map((s) => s.id) || [];
            this.selectedServices = filterServices(selectedIds, employeeIdFromLocal);
            if (!this.selectedServices.length) {
                clearServices();
            }
        } else if (source === EmployeeSelectionSource.Confirm) {
            const selectedIds = this.selectedServices?.map((s) => s.id) || [];
            this.selectedServices = filterServices(selectedIds, employeeId);
            if (!this.selectedServices.length) {
                clearServices();
            }
        }
    }

    private resetSelectedEmployee(employeeId: string, source: EmployeeSelectionSource) {
        this.selectedEmployee = this.employees.find((e) => e.id === employeeId) || this.allEmployeesObject;
        const isFirstAvailableEmployee = this.selectedEmployee.id === this.allEmployeesObject.id;
        const employeeIdFromLocal = this.appointmentRequest?.employee?.[0]?.id;
        const hasServiceFromLocal = !!this.appointmentRequest?.service?.length;

        if (!isFirstAvailableEmployee || source === EmployeeSelectionSource.Confirm) {
            return this.setEmployeeToLocalStorage();
        }

        if (source === EmployeeSelectionSource.Dropdown) {
            if (employeeIdFromLocal !== employeeId || (employeeId === this.allEmployeesObject.id && !hasServiceFromLocal)) {
                this.localStorageService.removeNested(APPOINTMENT_REQUEST, LSKey.Employee);
            } else {
                this.setEmployeeToLocalStorage();
            }
        }
    }

    private resetFilteredServiceList() {
        this.filteredServiceList = [...this.serviceList].sort((a, b) => a.name.localeCompare(b.name));
        if (this.selectedEmployee.id !== this.allEmployeesObject.id) {
            this.filteredServiceList = this.filteredServiceList.filter((x) => x.employeeIDList?.includes(this.selectedEmployee.id));
        }
    }

    private isFirstAvailableSelected(): boolean {
        return this.selectedEmployee.id === this.allEmployeesObject.id;
    }

    getServiceDuration(service: Service): number {
        if (!service) {
            return 0;
        }
        if (this.isFirstAvailableSelected()) {
            return service.duration;
        }
        const durationException = this.selectedEmployee?.durationExceptionList?.find((de) => de.serviceID === service.id);
        return durationException?.duration ?? service.duration;
    }

    getServiceDurationDisplay(service: Service): number {
        // For "First Available" (All Employees) with duration exceptions, return longest duration
        if (this.isFirstAvailableSelected() && service.hasDurationException) {
            return service.longestDuration ?? service.duration;
        }
        // Otherwise show the actual duration
        return this.getServiceDuration(service);
    }

    getServiceDurationPrefix(service: Service): string {
        // For "All Employees" (First Available) with duration exceptions, show "up to" prefix
        return this.isFirstAvailableSelected() && service.hasDurationException ? 'up to' : '';
    }

    getServicePrice(service: Service): number {
        if (this.isFirstAvailableSelected()) {
            return service.lowestPrice ?? service.price;
        }
        return this.selectedEmployee.employeeServicePriceList.find((s) => s.serviceID === service.id)?.price ?? 0;
    }

    getServicePriceDisplay(service: Service): string {
        // For "All Employees" (First Available) with price exceptions, show "starts at" message
        return this.isFirstAvailableSelected() && service.hasPriceException ? 'starts at' : '';
    }

    assignSelectedEmployee(selectedEmployeeId: number) {
        this.selectedEmployee = this.employees.find((e) => e.id === selectedEmployeeId.toString()) ?? this.allEmployeesObject;
    }

    onCancelEmployeeSelectionSheet() {
        this.selectedServices = [];
        this.selectedEmployee = this.allEmployeesObject; // Set default to "All Employees"
        this.localStorageService.removeNested(APPOINTMENT_REQUEST, LSKey.Service);
        this.localStorageService.removeNested(APPOINTMENT_REQUEST, LSKey.Employee);
        this.appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        this.employeeSelectionSheet()?.close();
    }

    onConfirm() {
        if (this.selectedEmployee) {
            this.onEmployeeChange(this.selectedEmployee.id, EmployeeSelectionSource.Confirm);
            this.setSelectedServicesFormGroupValue();
            this.employeeSelectionSheet()?.close();
        }
    }

    private setEmployeeToLocalStorage() {
        const employee = { ...this.selectedEmployee };
        if (employee?.id === this.allEmployeesObject.id) {
            Object.assign(employee, { firstAvailable: true, name: 'First Available' });
        }
        const selectedServiceIds = this.selectedServices?.map((s) => s.id);
        if (selectedServiceIds && employee?.employeeServicePriceList) {
            employee.employeeServicePriceList = employee.employeeServicePriceList.filter((e) => selectedServiceIds.includes(e.serviceID));
        }
        this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Employee, [employee]);
    }

    onChooseDateTime() {
        this.onConfirm();
        const customerID = this.localStorageService.getString(CUSTOMER_ID);
        this.router.navigate(['/booking/datetime'], {
            queryParams: {
                DSID: customerID,
            },
        });
    }
}
